import { ChevronDown, Download, Menu, MoonStar, Sparkles, SunMedium } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle, SheetDescription, SheetTrigger } from "@/components/ui/sheet";
import { NotificationPanel } from "@/components/layout/NotificationPanel";
import { ActivityFeedPage } from "@/components/ActivityFeedPage";
import { CourtyardsPage } from "@/components/CourtyardsPage";
import { FundsTravelPage } from "@/components/FundsTravelPage";
import { GatheringsPage } from "@/components/GatheringsPage";
import { CarePage } from "@/components/CarePage";
import { CommunitySetupPage } from "@/components/CommunitySetupPage";
import { HealthDashboardPage } from "@/components/HealthDashboardPage";
import { HomePage } from "@/components/HomePage";
import { todayFailure } from "@/lib/todayRecovery";
import { KinshipMapPage } from "@/components/KinshipMapPage";
import { LegacyThreadsPage } from "@/components/LegacyThreadsPage";
import { MemoryVaultPage } from "@/components/MemoryVaultPage";
import { MembersPage } from "@/components/MembersPage";
import { PilotCohortPage } from "@/components/PilotCohortPage";
import { PollsPage } from "@/components/PollsPage";
import { SettingsPage } from "@/components/SettingsPage";
import { StewardPage } from "@/components/StewardPage";
import { StrategyPage } from "@/components/StrategyPage";
import { SubscriptionPage } from "@/components/SubscriptionPage";
import { ThreadsPage } from "@/components/ThreadsPage";
import { TimelinePage } from "@/components/TimelinePage";
import { EventsPage } from "@/components/EventsPage";
import { GatheringDetailPage } from "@/components/GatheringDetailPage";
import { GatheringProposalsPage } from "@/components/GatheringProposalsPage";
import { CourtyardDetailPage } from "@/components/CourtyardDetailPage";
import { apiRequest } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { isStandalone, setupInstallPrompt, triggerInstall } from "@/lib/sw-register";

const ROLE_ORDER = { member: 1, organizer: 2, host: 3 };

// Existing routes remain intact. Today chooses the small primary set; the rest
// stay discoverable under More without advertising organizer-only surfaces.
const navItems = [
  { label: "Today", path: "/home", category: "today" },
  { label: "Ubuntu Guide", path: "/steward", module: "steward" },
  { label: "Community Health", path: "/health", module: "health", minimumRole: "organizer" },
  { label: "Activity", path: "/activity", category: "activity" },
  { label: "Courtyards", path: "/courtyards" },
  { label: "Timeline", path: "/timeline", module: "memory" },
  { label: "Memory Vault", path: "/memories", module: "memory" },
  { label: "Gatherings", path: "/gatherings", module: "gatherings", category: "gatherings" },
  { label: "Gathering Proposals", path: "/proposals", category: "proposals" },
  { label: "Circle of Care", path: "/care", module: "care" },
  { label: "Legacy Threads", path: "/legacy-threads", module: "legacy_threads" },
  { label: "Kinship Map", path: "/kinship-map", module: "kinship" },
  { label: "Polls", path: "/polls", module: "polls" },
  { label: "Funds & Travel", path: "/funds-travel", module: "funds" },
  { label: "Family activation", path: "/family/activate", category: "family_activation", minimumRole: "organizer" },
  { label: "Community Setup", path: "/setup", minimumRole: "organizer" },
  { label: "Subscription", path: "/subscription", minimumRole: "host" },
  { label: "Pilot cohort", path: "/pilot-cohort", adminOnly: true },
  { label: "Settings", path: "/settings" },
];

export const AppShell = ({ token, user, community, onLogout, onSessionRefresh }) => {
  const { resolvedTheme, setTheme } = useTheme();
  const location = useLocation();
  const [unreadSummary, setUnreadSummary] = useState({ announcements_unread: 0, chat_unread: 0, total_unread: 0 });
  const [myCommunities, setMyCommunities] = useState([]);
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [canInstall, setCanInstall] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [enabledModules, setEnabledModules] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [todayData, setTodayData] = useState(null);
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayError, setTodayError] = useState("");

  const refreshUnreadSummary = useCallback(async () => {
    try {
      const payload = await apiRequest("/communications/unread-summary", { token });
      setUnreadSummary(payload);
    } catch {
      setUnreadSummary({ announcements_unread: 0, chat_unread: 0, total_unread: 0 });
    }
  }, [token]);

  const loadMyCommunities = useCallback(async () => {
    try {
      const payload = await apiRequest("/communities/mine", { token });
      setMyCommunities(payload.communities || []);
    } catch {
      /* ignore */
    }
  }, [token]);

  const loadToday = useCallback(async () => {
    setTodayLoading(true);
    setTodayError("");
    try {
      const payload = await apiRequest("/today", { token });
      setTodayData(payload);
    } catch (error) {
      setTodayData(null);
      setTodayError(todayFailure(error));
    } finally {
      setTodayLoading(false);
    }
  }, [token]);

  useEffect(() => { refreshUnreadSummary(); }, [location.pathname, refreshUnreadSummary]);
  useEffect(() => { loadMyCommunities(); }, [loadMyCommunities]);
  useEffect(() => { loadToday(); }, [community?.id, loadToday]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const payload = await apiRequest("/community/modules", { token });
        if (active) setEnabledModules(payload.enabled || []);
      } catch { /* on failure, leave all modules visible */ }
    })();
    return () => { active = false; };
  }, [token, community?.id]);

  useEffect(() => {
    if (!isStandalone()) setupInstallPrompt(() => setCanInstall(true));
  }, []);

  const handleInstall = async () => {
    const accepted = await triggerInstall();
    if (accepted) {
      setCanInstall(false);
      toast.success("Kindred installed to your device!");
    }
  };

  const handleSwitchCommunity = async (communityId) => {
    try {
      await apiRequest("/communities/switch", { method: "POST", token, data: { community_id: communityId } });
      setShowSwitcher(false);
      onSessionRefresh();
    } catch {
      /* ignore */
    }
  };

  const handleJoinCommunity = async () => {
    if (!joinCode.trim()) return;
    try {
      await apiRequest("/communities/join", { method: "POST", token, data: { invite_code: joinCode } });
      setJoinCode("");
      setShowSwitcher(false);
      onSessionRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to join community.");
    }
  };

  const viewerRole = todayData?.viewer_role ?? user?.role;
  const effectiveRole = viewerRole === "new_member" ? "member" : viewerRole;
  const visibleNavItems = navItems.filter((item) => {
    if (item.adminOnly && !user?.is_platform_admin) return false;
    if (item.module && enabledModules && !enabledModules.includes(item.module)) return false;
    if (item.minimumRole && (ROLE_ORDER[effectiveRole] || 0) < ROLE_ORDER[item.minimumRole]) return false;
    if (item.category === "family_activation" && todayData?.lifecycle_state !== "provisional") return false;
    return true;
  });
  const primaryCategories = new Set(todayData?.navigation_categories?.length ? todayData.navigation_categories : ["today", "gatherings", "activity"]);
  const primaryNavItems = visibleNavItems.filter((item) => item.category && primaryCategories.has(item.category));
  const moreNavItems = visibleNavItems.filter((item) => !primaryNavItems.includes(item));

  const navLink = (item) => (
    <NavLink
      className={({ isActive }) =>
        `rounded-2xl px-4 py-3 text-sm font-semibold transition duration-300 ${
          isActive
            ? "bg-primary text-primary-foreground shadow-[0_16px_36px_-22px_rgba(154,52,18,0.7)]"
            : "bg-background/70 text-foreground hover:bg-accent/70"
        }`
      }
      data-testid={`nav-link-${item.path.replace(/\//g, "") || "home"}`}
      key={item.path}
      onClick={() => setSidebarOpen(false)}
      to={item.path}
    >
      <span className="flex items-center justify-between gap-3">
        <span>{item.label}</span>
        {item.path === "/courtyards" && unreadSummary.total_unread > 0 ? (
          <span className="rounded-full bg-primary/15 px-2.5 py-1 text-xs font-semibold text-primary" data-testid="nav-link-courtyards-unread-badge">
            {unreadSummary.total_unread}
          </span>
        ) : null}
      </span>
    </NavLink>
  );

  const sidebarContent = (
    <>
            <nav aria-label="Family tools" className="order-first grid gap-2 lg:hidden" data-testid="mobile-navigation">
              {visibleNavItems.map(navLink)}
            </nav>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div>
                  <p className="eyebrow-text">Where your circles gather and grow.</p>
                  <h1 className="font-display text-2xl text-foreground">Kindred</h1>
                </div>
              </div>
              <div className="soft-panel space-y-2">
                <button
                  className="flex w-full items-center justify-between text-left"
                  data-testid="community-switcher-toggle"
                  onClick={() => setShowSwitcher(!showSwitcher)}
                >
                  <div>
                    <p className="text-lg font-semibold text-foreground" data-testid="shell-community-name">
                      {community?.name}
                    </p>
                    <p className="text-sm text-muted-foreground" data-testid="shell-community-metadata">
                      {community?.community_type} courtyard · {community?.location}
                    </p>
                  </div>
                  {myCommunities.length > 1 && (
                    <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showSwitcher ? "rotate-180" : ""}`} />
                  )}
                </button>
                <p className="text-sm text-muted-foreground" data-testid="shell-user-identity">
                  Signed in as {user?.full_name} ({user?.role})
                </p>
                {showSwitcher && (
                  <div className="mt-2 space-y-2 border-t border-border/50 pt-2" data-testid="community-switcher-panel">
                    {myCommunities.filter((c) => !c.is_active).map((c) => (
                      <button
                        className="flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm text-foreground hover:bg-accent/70 transition-colors"
                        data-testid={`switch-community-${c.id}`}
                        key={c.id}
                        onClick={() => handleSwitchCommunity(c.id)}
                      >
                        <span className="font-medium">{c.name}</span>
                        <span className="text-xs text-muted-foreground">{c.member_count} members</span>
                      </button>
                    ))}
                    <div className="flex gap-2 pt-1">
                      <Input
                        className="field-input h-8 text-xs flex-1"
                        data-testid="join-community-input"
                        onChange={(e) => setJoinCode(e.target.value)}
                        placeholder="Invite code"
                        value={joinCode}
                      />
                      <Button className="rounded-full h-8" data-testid="join-community-btn" disabled={!joinCode.trim()} onClick={handleJoinCommunity} size="sm" variant="secondary">
                        Join
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <nav className="hidden gap-2 lg:grid" data-testid="shell-navigation">
              {primaryNavItems.map(navLink)}
              {moreNavItems.length ? (
                <div className="grid gap-2">
                  <button
                    aria-controls="shell-more-navigation"
                    aria-expanded={moreOpen}
                    className="flex items-center justify-between rounded-2xl bg-background/70 px-4 py-3 text-left text-sm font-semibold text-foreground transition hover:bg-accent/70"
                    data-testid="nav-more-toggle"
                    onClick={() => setMoreOpen((value) => !value)}
                    type="button"
                  >
                    More
                    <ChevronDown className={`h-4 w-4 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
                  </button>
                  {moreOpen ? <div className="grid gap-2 border-l border-border/60 pl-3" id="shell-more-navigation" data-testid="nav-more-items">{moreNavItems.map(navLink)}</div> : null}
                </div>
              ) : null}
            </nav>

            <div className="flex flex-wrap items-center gap-3">
              <NotificationPanel token={token} />
              <Button
                className="rounded-full"
                data-testid="theme-toggle-button"
                onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                variant="outline"
              >
                {resolvedTheme === "dark" ? <SunMedium className="h-4 w-4" /> : <MoonStar className="h-4 w-4" />}
                {resolvedTheme === "dark" ? "Light mode" : "Dark mode"}
              </Button>
              {canInstall && (
                <Button
                  className="rounded-full w-full"
                  data-testid="pwa-install-btn"
                  onClick={handleInstall}
                  variant="outline"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Install Kindred
                </Button>
              )}
              <Button
                className="rounded-full"
                data-testid="logout-button"
                onClick={onLogout}
                variant="secondary"
              >
                Sign out
              </Button>
            </div>

            <div className="border-t border-border/40 pt-4 flex gap-4" data-testid="shell-legal-links">
              <NavLink className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="sidebar-privacy-link" to="/privacy">
                Privacy
              </NavLink>
              <NavLink className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="sidebar-terms-link" to="/terms">
                Terms
              </NavLink>
              <NavLink className="text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="sidebar-support-link" to="/support">
                Support
              </NavLink>
            </div>
    </>
  );

  return (
    <div className="app-canvas min-h-screen pb-10" style={{ paddingLeft: "env(safe-area-inset-left, 0px)", paddingRight: "env(safe-area-inset-right, 0px)" }}>
      <div className="page-section pt-6 md:pt-8">
        <div className="mb-4 flex items-center justify-between lg:hidden" style={{ paddingTop: "calc(env(safe-area-inset-top, 20px) + 8px)" }}>
          <div className="flex items-center gap-3">
            <Sparkles className="h-5 w-5 text-primary" />
            <h1 className="font-display text-xl text-foreground">Kindred</h1>
          </div>
          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetTrigger asChild>
              <Button aria-label="Open family menu" className="rounded-full" data-testid="mobile-menu-toggle" size="sm" variant="outline">
                <Menu className="h-4 w-4" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent className="flex w-[min(90vw,24rem)] flex-col gap-6 overflow-y-auto pt-[calc(env(safe-area-inset-top,0px)+3rem)] pb-[calc(env(safe-area-inset-bottom,0px)+1.5rem)] [&>button]:top-[calc(env(safe-area-inset-top,0px)+1rem)]" data-testid="mobile-menu-drawer">
              <SheetTitle className="sr-only">Kindred family menu</SheetTitle>
              <SheetDescription className="sr-only">Choose a family tool or manage your account.</SheetDescription>
              {sidebarContent}
            </SheetContent>
          </Sheet>
        </div>

        <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="archival-card hidden h-fit flex-col gap-6 lg:sticky lg:top-6 lg:flex">
            {sidebarContent}
          </aside>

          <main className="space-y-6">
            <div className="archival-card flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="eyebrow-text">Where your circles gather and grow.</p>
                <h2 className="font-display text-3xl text-foreground sm:text-4xl" data-testid="shell-header-title">
                  Your family space, without the noise.
                </h2>
                <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">
                  Start with what matters today. Open the menu for your family tools.
                </p>
              </div>
              <div className="soft-panel flex items-center gap-3 self-start" data-testid="shell-value-pill">
                <Sparkles aria-hidden="true" className="h-4 w-4 text-primary" />
                <div>
                  <p className="text-sm font-semibold text-foreground">Digital sovereignty</p>
                  <p className="text-xs text-muted-foreground">No feed. No ads. No algorithm.</p>
                </div>
              </div>
            </div>

            <Routes>
              <Route element={<HomePage onSignInAgain={onLogout} onRetryToday={loadToday} todayData={todayData} todayError={todayError} todayLoading={todayLoading} token={token} />} path="dashboard" />
              <Route element={<HomePage onSignInAgain={onLogout} onRetryToday={loadToday} todayData={todayData} todayError={todayError} todayLoading={todayLoading} token={token} />} path="home" />
              <Route element={<StewardPage token={token} />} path="steward" />
              <Route element={<HealthDashboardPage token={token} />} path="health" />
              <Route element={<CarePage token={token} user={user} />} path="care" />
              <Route element={<CommunitySetupPage token={token} user={user} />} path="setup" />
              <Route element={<ActivityFeedPage token={token} />} path="activity" />
              <Route element={<CourtyardsPage onCommunicationsViewed={refreshUnreadSummary} token={token} user={user} />} path="courtyards" />
              <Route element={<CourtyardDetailPage onCommunicationsViewed={refreshUnreadSummary} token={token} user={user} />} path="courtyards/:id" />
              <Route element={<TimelinePage token={token} />} path="timeline" />
              <Route element={<GatheringsPage token={token} user={user} />} path="gatherings" />
              <Route element={<GatheringDetailPage token={token} user={user} />} path="gatherings/:id" />
              <Route element={<GatheringProposalsPage token={token} user={user} />} path="proposals" />
              <Route element={<PollsPage token={token} user={user} />} path="polls" />
              <Route element={<FundsTravelPage token={token} user={user} />} path="funds-travel" />
              <Route element={<SettingsPage onSessionRefresh={onSessionRefresh} token={token} user={user} />} path="settings" />
              <Route element={<SubscriptionPage token={token} user={user} />} path="subscription" />
              <Route
                element={<MembersPage onSessionRefresh={onSessionRefresh} token={token} user={user} />}
                path="members"
              />
              <Route element={<MemoryVaultPage token={token} user={user} />} path="memories" />
              <Route element={user?.is_platform_admin ? <PilotCohortPage token={token} user={user} /> : <Navigate replace to="/home" />} path="pilot-cohort" />
              <Route element={<LegacyThreadsPage token={token} user={user} />} path="legacy-threads" />
              <Route element={<KinshipMapPage token={token} />} path="kinship-map" />
              <Route element={<ThreadsPage token={token} user={user} />} path="threads" />
              <Route element={<EventsPage token={token} user={user} />} path="events" />
              <Route element={user?.is_platform_admin ? <StrategyPage mode="app" /> : <Navigate replace to="/home" />} path="app-strategy" />
              <Route element={<Navigate replace to="/home" />} path="*" />
            </Routes>
          </main>
        </div>
      </div>
    </div>
  );
};
